/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import { signal } from '@angular/core';

const LOADING_DELAY_MS = 150;
const ANIMATION_MS = 1200;

// Shared state for the feed card's action buttons: a loading flag that only turns on for slow
// requests (so fast ones don't flicker) and a short-lived completion animation flag.
export class ActionState {
  readonly busy = signal(false);
  readonly loading = signal(false);
  readonly animation = signal<'on' | 'off' | null>(null);
  readonly announcement = signal('');
  // After a toggle, hover previews ("Unsubscribe", "Show") wait until the pointer leaves the button,
  // so the label confirms what just happened instead of offering to undo it.
  readonly hoverLocked = signal(false);

  private animationTimer: ReturnType<typeof setTimeout> | undefined;

  run<T>(request: Promise<T>): Promise<T> {
    this.busy.set(true);
    const loadingTimer = setTimeout(() => this.loading.set(true), LOADING_DELAY_MS);
    return request.finally(() => {
      clearTimeout(loadingTimer);
      this.loading.set(false);
      this.busy.set(false);
    });
  }

  complete(direction: 'on' | 'off', announcement: string): void {
    clearTimeout(this.animationTimer);
    this.animation.set(direction);
    this.hoverLocked.set(true);
    this.announcement.set(announcement);
    this.animationTimer = setTimeout(() => this.animation.set(null), ANIMATION_MS);
  }

  unlockHover(): void {
    this.hoverLocked.set(false);
  }
}
